import React, { useState } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import {
  User as UserIcon, Brain, Shield, Zap, TrendingUp, Users,
  Target, FileText, Settings, Heart, MessageSquare, Briefcase,
  MapPin, CheckCircle, Scale, Megaphone, Flag, ListOrdered, Clock,
  TrendingDown, BarChart3, AlertTriangle, Check, X, Compass,
  Award, Sliders, ThumbsUp, GitMerge, FileWarning, Eye, GitPullRequest,
  CheckSquare, Siren, Activity, Bot, Star,
  ThumbsDown, Filter, ArrowUpDown, ChevronDown, ChevronUp, BookOpen,
  ShieldCheck, Crosshair
} from 'lucide-react';

// --- HELPER FUNCTIONS ---

export const formatStrategicTier = (tier) => {
  if (!tier) return "Unknown Status";
  if (tier === "TIER_1_GAIN") return "Priority: Gain";
  if (tier === "TIER_2_GAIN") return "Target: Gain"; 
  if (tier.includes("CRITICAL")) return "Critical: Defend";
  if (tier.includes("URGENT")) return "Urgent: Defend";
  if (tier.includes("DEFEND")) return "Priority: Defend";
  if (tier.includes("WATCH")) return "Watch List";
  if (tier.includes("SAFE")) return "Safe Hold";
  return "Long-Term Target";
};

export const LABEL = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "Unknown";
  const v = Number(n);
  if (v >= 0.85) return "Very High";
  if (v >= 0.70) return "High";
  if (v >= 0.55) return "Moderate";
  if (v >= 0.40) return "Low";
  return "Very Low";
};

export const reliabilityLabel = (r) => {
  if (r === null || r === undefined) return "Unknown";
  if (r >= 0.85) return "Very Reliable";
  if (r >= 0.70) return "Reliable";
  if (r >= 0.50) return "Somewhat Reliable";
  return "Unreliable";
};

// --- SHARED UI COMPONENTS ---

export const Panel = ({ title, icon: Icon, children, className = "", collapsible = false, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`bg-slate-800/50 border border-slate-700/50 rounded-xl shadow-xl ${className}`}
    >
      {title && (
        <div 
          onClick={() => collapsible && setIsOpen(!isOpen)}
          className={`flex items-center justify-between px-6 pt-6 pb-4 ${collapsible ? 'cursor-pointer hover:bg-slate-800/50 transition-colors rounded-t-xl' : ''}`}
        >
          <h3 className="flex items-center text-lg font-semibold text-white">
            {Icon && <Icon size={20} className="mr-2 text-accent-400" />}
            <span className="ml-0">{title}</span>
          </h3>
          {collapsible && (
            <div className="text-slate-400">
              {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          )}
        </div>
      )}
      <AnimatePresence>
        {(!collapsible || isOpen) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className={title ? "px-4 sm:px-6 pb-4 sm:pb-6" : "p-4 sm:p-6"}>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const ValueBadge = ({ value, type = "" }) => {
  const configs = {
    // Strategic Tiers
    "Priority: Gain": { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: TrendingUp },
    "Target: Gain": { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Zap },
    "Opportunity: Gain": { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Zap },
    "Critical: Defend": { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertTriangle },
    "Urgent: Defend": { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: ShieldCheck },
    "Priority: Defend": { color: 'bg-sky-500/20 text-sky-400 border-sky-500/30', icon: Shield },
    "Long-Term Target": { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Crosshair },
    "Safe Hold": { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
    "Unwinnable": { color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', icon: X },
    "Watch List": { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Eye },
    "No Contest": { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: FileWarning },

    // Labels
    "Very High": { color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: TrendingUp },
    "High": { color: 'bg-teal-500/10 text-teal-400 border-teal-500/20', icon: TrendingUp },
    "Moderate": { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', icon: Zap },
    "Low": { color: 'bg-orange-500/10 text-orange-400 border-orange-500/20', icon: TrendingDown },
    "Very Low": { color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: TrendingDown },
    
    // Probabilities
    "Very Likely": { color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: CheckCircle },
    "Likely": { color: 'bg-teal-500/10 text-teal-400 border-teal-500/20', icon: CheckCircle },
    "Possible": { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', icon: CheckCircle },
    "Unlikely": { color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: X },
    
    // Reliability
    "Very Reliable": { color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: Shield },
    "Reliable": { color: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20', icon: Shield },
    "Somewhat Reliable": { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', icon: Shield },
    "Unreliable": { color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: Shield },

    // Ward/Risk Badges
    "Synergy": { color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: Check },
    "Mismatch": { color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: X },
    "Adapt": { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', icon: AlertTriangle },

    // Compatibility Levels
    "Exceptional - Near-perfect resonance": { color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: CheckCircle },
    "Excellent - Natural alignment": { color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: CheckCircle },
    "Very Good - Strong compatibility": { color: 'bg-teal-500/10 text-teal-400 border-teal-500/20', icon: Check },
    "Good - Moderate alignment": { color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: Check },
    "Fair - Some common ground": { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', icon: Sliders },
    "Challenging - Significant mismatch": { color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: X },

    "Excellent": { color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: CheckCircle },
    "Good": { color: 'bg-teal-500/10 text-teal-400 border-teal-500/20', icon: Check },
    "Moderate": { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', icon: Sliders },
    "Challenging": { color: 'bg-orange-500/10 text-orange-400 border-orange-500/20', icon: AlertTriangle },
    "Difficult": { color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: X },

    // Moral Frames
    "Care": { color: 'bg-green-500/10 text-green-400 border-green-500/20', icon: Heart },
    "Fairness": { color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: Scale },
    "Loyalty": { color: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20', icon: Users },
    "Authority": { color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', icon: Shield },
    "Sanctity": { color: 'bg-purple-500/10 text-purple-400 border-purple-500/20', icon: CheckCircle },
    "Liberty": { color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', icon: Flag },

    "Unknown": { color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', icon: FileText }
  };

  const label = value || "Unknown";
  const config = configs[label] || configs[label.charAt(0).toUpperCase() + label.slice(1)] || configs.Unknown;
  const Icon = config.icon;
  const badgeType = type ? ` ${type}` : '';

  return (
    <div className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      <Icon size={12} />
      <span>{label}{badgeType}</span>
    </div>
  );
};

export const StatCard = ({ title, value, icon: Icon, colorClass = "text-white" }) => (
  <div className="flex flex-col items-start bg-slate-700/30 rounded-lg p-3 sm:p-4 transition-all hover:bg-slate-700/50 min-w-0">
    <div className="flex items-center mb-1">
      <Icon size={16} className={`mr-2 ${colorClass}`} />
      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{title}</span>
    </div>
    <div className={`text-base sm:text-xl font-bold ${colorClass} truncate`}>{value}</div>
  </div>
);

// --- METRIC ITEM (Clean, No Icons, Typographic Focus) ---
export const MetricItem = ({ label, value, colorClass = "text-white" }) => (
  <div className="flex-1 min-w-[70px]">
    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 block mb-0.5">
      {label}
    </span>
    <p className={`text-xl font-bold ${colorClass}`}>{value}</p>
  </div>
);

// --- WARD PERFORMANCE CARD (Compact, Typographic Layout) ---
export const WardPerformanceCard = ({ ward, isSelected = false, onClick }) => {
  const strategicStatus = formatStrategicTier(ward?.artemis_strategic_tier); 
  const targetPool = ward?.bq_persuadable_voters || ward?.bq_swing_pool_size || null;
  const percent = (val) => (val !== null && val !== undefined) ? `${(val * 100).toFixed(0)}%` : 'N/A';

  const getTheme = (status) => {
    const defaults = { border: 'border-slate-600', shadow: 'shadow-slate-500/50' };
    if (!status) return defaults;
    const s = status.toLowerCase();

    if (s.includes('priority: gain')) return { border: 'border-yellow-500', shadow: 'shadow-yellow-500/50' };
    if (s.includes('target: gain') || s.includes('opportunity')) return { border: 'border-amber-400', shadow: 'shadow-amber-400/50' };
    if (s.includes('critical: defend')) return { border: 'border-red-600', shadow: 'shadow-red-600/50' };
    if (s.includes('urgent: defend')) return { border: 'border-orange-500', shadow: 'shadow-orange-500/50' };
    if (s.includes('priority: defend')) return { border: 'border-sky-500', shadow: 'shadow-sky-500/50' };
    if (s.includes('long-term') || s.includes('standard')) return { border: 'border-blue-400', shadow: 'shadow-blue-400/50' };
    if (s.includes('safe hold')) return { border: 'border-green-500', shadow: 'shadow-green-500/50' };
    if (s.includes('unwinnable')) return { border: 'border-slate-500', shadow: 'shadow-slate-500/50' };
    if (s.includes('watch') || s.includes('contest')) return { border: 'border-purple-500', shadow: 'shadow-purple-500/50' };

    return defaults;
  };

  const theme = getTheme(strategicStatus);

  return (
    <div
      className={`
        flex-shrink-0 w-64 sm:w-80 p-3 sm:p-4 rounded-xl border-2 text-left transition-all duration-300 relative flex flex-col justify-between
        ${isSelected
          ? `bg-slate-800 ${theme.border} shadow-[0_0_20px_rgba(0,0,0,0.3)] ${theme.shadow}` 
          : 'bg-slate-800/40 border-slate-700/50 hover:bg-slate-800 hover:border-slate-500'
        }
      `}
      onClick={() => onClick && onClick(ward)}
    >
      <div>
        <div className="flex justify-between items-start mb-2">
          <h4 className="font-bold text-white text-base sm:text-lg line-clamp-2 leading-tight flex items-center">
             {ward?.ward_name || "Unknown Ward"}
          </h4>
          {isSelected && <CheckCircle size={20} className="text-accent-400 flex-shrink-0 ml-2 mt-0.5" />}
        </div>
        
        {/* --- Updated Middle Row: Badge + Persuadable Header --- */}
        <div className="flex items-end justify-between mb-3">
          <ValueBadge value={strategicStatus} />
          {targetPool && (
            <div className="text-right">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 block mb-0.5">
                Persuadable
              </span>
              <span className="text-white font-bold text-sm">
                {targetPool}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="pt-3 border-t border-slate-700/50 mt-auto">
        <div className="flex items-center justify-between">
          <MetricItem 
            label="Fit" 
            value={percent(ward?.scoring_factors?.minerva_candidate_compatibility)}
            colorClass="text-accent-400"
          />
          <MetricItem 
            label="Winability" 
            value={percent(ward?.scoring_factors?.artemis_winability_score)}
            colorClass="text-green-400"
          />
          <MetricItem 
            label="Priority" 
            value={percent(ward?.combined_priority_score)}
            colorClass="text-yellow-400"
          />
        </div>
      </div>
    </div>
  );
};

export const PriorityAlerts = ({ alerts }) => {
  if (!alerts || alerts.length === 0) return null;

  const alertConfigs = {
    danger: { icon: Siren, bgColor: 'bg-red-900/30', borderColor: 'border-red-500', textColor: 'text-red-400', title: 'Strategic Alert' },
    success: { icon: Zap, bgColor: 'bg-green-900/30', borderColor: 'border-green-500', textColor: 'text-green-400', title: 'High-Leverage Opportunity' },
    warning: { icon: AlertTriangle, bgColor: 'bg-yellow-900/30', borderColor: 'border-yellow-500', textColor: 'text-yellow-400', title: 'Corroborated Priority' },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
      className="space-y-4"
    >
      {alerts.map((alert, i) => {
        const config = alertConfigs[alert.type] || alertConfigs.warning;
        const Icon = config.icon;
        return (
          <div key={i} className={`flex items-start space-x-4 p-4 rounded-lg border-l-4 ${config.bgColor} ${config.borderColor}`}>
            <Icon size={20} className={`flex-shrink-0 mt-0.5 ${config.textColor}`} />
            <div>
              <h4 className={`text-lg font-bold ${config.textColor}`}>{config.title}</h4>
              <p className="text-sm text-slate-300">{alert.message}</p>
            </div>
          </div>
        );
      })}
    </motion.div>
  );
};

export const PsychologicalPortrait = ({ profile }) => {
  if (!profile) return null;
  const insights = [];

  if (profile.extraversion < 0.5) insights.push("Tends to be reserved and analytical over outwardly charismatic.");
  else insights.push("An energetic and outgoing presence, comfortable engaging crowds.");
  
  if (profile.openness < 0.4) insights.push("Prefers evidence-based, established methods over novel or abstract ideas.");
  else if (profile.openness > 0.7) insights.push("Highly creative and open to new, unconventional approaches.");

  if (profile.conscientiousness > 0.8) insights.push("Extremely diligent, reliable, and detail-oriented; a methodical planner.");
  else if (profile.conscientiousness < 0.4) insights.push("A flexible and spontaneous actor, may dislike rigid structures.");
  
  if (profile.agreeableness > 0.7) insights.push("A natural consensus-builder, highly cooperative and empathetic.");
  else if (profile.agreeableness < 0.4) insights.push("Willing to challenge others and engage in necessary confrontation.");
  
  if (profile.neuroticism > 0.6) insights.push("Passionate and emotionally expressive, but may be sensitive to criticism.");
  else if (profile.neuroticism < 0.3) insights.push("Possesses high emotional stability and remains calm under pressure.");

  return (
    <div className="mt-6 bg-slate-900/50 p-4 rounded-lg border border-slate-700">
      <h4 className="font-semibold text-white flex items-center mb-3">
        <Eye size={16} className="mr-2 text-purple-400"/> Psychological Portrait
      </h4>
      <ul className="space-y-2 list-disc list-inside text-sm text-slate-300 pl-2">
        {insights.map((insight, i) => <li key={i}>{insight}</li>)}
      </ul>
    </div>
  );
};

export const SynthesizedAlignment = ({ theiaAnalysis, gapAnalysis }) => {
  const topResonant = theiaAnalysis?.campaign_resonance_analysis?.top_resonant_archetypes || [];
  const lowResonant = theiaAnalysis?.campaign_resonance_analysis?.lowest_resonance_archetypes || [];
  const allGaps = gapAnalysis?.gaps || [];

  if (topResonant.length === 0 && lowResonant.length === 0) {
    return <p className="text-slate-500">No THEIA resonance analysis available.</p>;
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2">
      <div className="space-y-3">
        <h4 className="font-semibold text-white flex items-center">
          <ThumbsUp size={16} className="mr-2 text-green-400"/> Top Resonant Archetypes
        </h4>
        {topResonant.length > 0 ? topResonant.slice(0, 2).map((arch) => (
          <div key={arch.archetype_name} className="p-3 bg-slate-700/30 rounded-lg border border-slate-700">
            <strong className="text-green-400">{arch.archetype_name}</strong>
            <p className="text-xs text-slate-400">{arch.main_segment}</p>
            <div className="mt-2"><ValueBadge value={arch.compatibility_level} /></div>
            <p className="text-xs text-slate-500 mt-2">Natural alignment. Use the 'North Star Narrative' without modification.</p>
          </div>
        )) : <p className="text-slate-500 text-sm">No top archetypes identified.</p>}
      </div>

      <div className="space-y-3">
        <h4 className="font-semibold text-white flex items-center">
          <ThumbsDown size={16} className="mr-2 text-red-400"/> Lowest Resonance (Pivot Required)
        </h4>
        {lowResonant.length > 0 ? lowResonant.slice(0, 2).map((arch) => {
          const gap = allGaps.find(g => g.target_archetype === arch.archetype_name);
          return (
            <div key={arch.archetype_name} className="p-3 bg-slate-700/30 rounded-lg border border-slate-700">
              <strong className="text-red-400">{arch.archetype_name}</strong>
              <p className="text-xs text-slate-400">{arch.main_segment}</p>
              <div className="mt-2"><ValueBadge value={arch.compatibility_level} /></div>
              {gap ? (
                <div className="mt-2 pt-2 border-t border-slate-600">
                  <p className="text-xs text-yellow-400"><strong>Pivot:</strong> {gap.recommended_pivot}</p>
                </div>
              ) : <p className="text-xs text-slate-500 mt-2">Significant mismatch. Check ward-level analysis for pivots.</p>}
            </div>
          );
        }) : <p className="text-slate-500 text-sm">No low-resonance archetypes identified.</p>}
      </div>
    </div>
  );
};

export const GenerativeScript = ({ wardRisk, candidateProfile }) => {
  if (!wardRisk || !candidateProfile) return null;

  let scriptTitle = "Authenticity Pivot Script (THEIA)";
  let script = "No specific pivot script generated for this context.";
  let scriptRationale = "No THEIA-diagnosed authenticity gaps. Standard talking points apply.";

  const authRisk = (wardRisk.risks_identified || []).find(r => r.source === 'MINERVA (THEIA)' && r.risk.includes('Authenticity Gap'));

  if (authRisk) {
    script = authRisk.recommendation || script;
    scriptRationale = authRisk.details || scriptRationale;
  }
  
  return (
    <div className="pt-4 mt-4 border-t border-slate-700">
      <h4 className="font-semibold text-white mb-3 flex items-center"><Bot size={16} className="mr-2 text-purple-400" /> {scriptTitle}</h4>
      <div className="p-3 rounded-lg bg-slate-900/50 border border-slate-700">
        <p className="text-sm text-slate-200 italic">"{script}"</p>
      </div>
      <p className="text-xs text-slate-500 mt-2"><strong>Rationale:</strong> {scriptRationale}</p>
    </div>
  );
};

export const StrategicFeedbackKQI = ({ wardRisk }) => {
  if (!wardRisk) return null;

  const cynicismRisk = (wardRisk.risks_identified || []).find(r => r.source === 'ARTEMIS (Psychographic)' && r.risk.includes('Cynicism'));
  const gotvRisk = (wardRisk.risks_identified || []).find(r => r.source === 'ARTEMIS (Psychographic)' && r.risk.includes('GOTV'));
  const authRiskPivot = (wardRisk.risks_identified || []).find(r => r.source === 'MINERVA (THEIA)' && r.recommendation)?.recommendation;

  let goal, failureKQI, successKQI;

  if (cynicismRisk) {
    goal = `Your goal is not agreement; it is to shift the conversation from "Cynicism" to "Curiosity."`;
    failureKQI = `Voters repeating the concern: "${cynicismRisk.details}"`;
    successKQI = authRiskPivot ? `Voters engaging with the pivot: "What do you mean by '${authRiskPivot.split('Reframe the policy using ')[1]?.toLowerCase() || 'this'}'?"` : "Voters asking follow-up questions about the specific plan.";
  } else if (gotvRisk) {
    goal = `Your goal is not persuasion; it is "Active Mobilization."`;
    failureKQI = `Voters saying "I agree with you." (This is passive).`;
    successKQI = `Voters asking "How can I help?" or "Have you spoken to my neighbour?" (This is active).`;
  } else {
    goal = `Your goal is to build authentic connection.`;
    failureKQI = `Receiving passive, non-committal agreement.`;
    successKQI = `Voters asking specific, follow-up questions.`;
  }
  
  return (
    <div className="pt-4 mt-4 border-t border-slate-700">
      <h4 className="font-semibold text-white mb-3 flex items-center"><Activity size={16} className="mr-2 text-teal-400" /> Strategic Feedback (KQI)</h4>
      <p className="text-sm text-slate-300 mb-3 italic">{goal}</p>
      <ul className="space-y-2 text-sm">
        <li className="flex items-start space-x-2"><X size={16} className="text-red-400 mt-1 flex-shrink-0" /><div><strong className="text-red-400">Listen For (Failure):</strong><p className="text-slate-400">{failureKQI}</p></div></li>
        <li className="flex items-start space-x-2"><Check size={16} className="text-green-400 mt-1 flex-shrink-0" /><div><strong className="text-green-400">Listen For (Success):</strong><p className="text-slate-400">{successKQI}</p></div></li>
      </ul>
    </div>
  );
};